import axios from 'axios';
import type { PaginatedResponse, TinyUser, User } from './types';

export type MediaApplicationStatus = 'submitted' | 'under_review' | 'needs_info' | 'accepted' | 'declined' | 'closed';
export type MediaTier = 'trial' | 'creator' | 'partner';
export type MediaLicenseProduct = 'basic' | 'advanced';
export type MediaProgramStatus = 'onboarding' | 'active' | 'follow_up_required' | 'paused' | 'completed';

export interface MediaAvailability {
    applications_open: boolean;
    closed_message: string | null;
}

export interface MediaApplicationUpdate {
    id: number;
    media_application_id: number;
    user_id: number | null;
    type: 'message' | 'content' | 'status' | 'follow_up';
    body: string | null;
    content_url: string | null;
    internal: boolean;
    approved_by: number | null;
    approved_at: string | null;
    license_extended_until: string | null;
    reviewer: TinyUser | null;
    user: TinyUser | null;
    created_at: string;
    updated_at: string;
}

export interface MediaApplication {
    id: number;
    user_id: number;
    platform: string;
    channel_name: string;
    channel_url: string;
    audience_size: number;
    average_views: number;
    content_language: string;
    content_categories: string;
    experience: string;
    motivation: string;
    content_plan: string;
    status: MediaApplicationStatus;
    assigned_tier: MediaTier | null;
    license_product: MediaLicenseProduct | null;
    license_expires_at: string | null;
    decision_reason: string | null;
    reviewed_by: number | null;
    reviewed_at: string | null;
    next_follow_up_at: string | null;
    program_status: MediaProgramStatus | null;
    created_at: string;
    updated_at: string;
    updates?: MediaApplicationUpdate[];
    visible_updates?: MediaApplicationUpdate[];
    updates_count?: number;
    pending_content_count?: number;
    user?: User;
    reviewer?: TinyUser | null;
}

export interface MediaApplicationPayload {
    platform: string;
    channel_name: string;
    channel_url: string;
    audience_size: number;
    average_views: number;
    content_language: string;
    content_categories: string;
    experience: string;
    motivation: string;
    content_plan: string;
}

export interface MediaUpdatePayload {
    type?: 'message' | 'content';
    body?: string;
    content_url?: string;
    internal?: boolean;
}

export const mediaApplications = {
    availability() {
        return axios.get<MediaAvailability>('media-applications/availability');
    },
    me() {
        return axios.get<{ application: MediaApplication | null; availability: MediaAvailability }>('media-applications/me');
    },
    create(payload: MediaApplicationPayload) {
        return axios.post<{ application: MediaApplication }>('media-applications', payload);
    },
    postUpdate(id: number, payload: MediaUpdatePayload) {
        return axios.post<{ update: MediaApplicationUpdate }>(`media-applications/${id}/updates`, payload);
    },
    list(status = 'submitted', limit = 50, page = 0) {
        return axios.get<PaginatedResponse<MediaApplication> & { settings: MediaAvailability }>('media-applications', {
            params: { status, limit, page },
        });
    },
    get(id: number) {
        return axios.get<{ application: MediaApplication }>(`media-applications/${id}`);
    },
    beginReview(id: number) {
        return axios.post<{ application: MediaApplication }>(`media-applications/${id}/review`);
    },
    requestInfo(id: number, reason: string) {
        return axios.post<{ application: MediaApplication }>(`media-applications/${id}/request-info`, { reason });
    },
    settings() {
        return axios.get<{ settings: MediaAvailability }>('media-applications/settings');
    },
    updateSettings(applications_open: boolean, closed_message?: string | null) {
        return axios.put<{ settings: MediaAvailability }>('media-applications/settings', { applications_open, closed_message });
    },
    accept(id: number, tier: MediaTier, license_product: MediaLicenseProduct | null, acceptance_note?: string, next_follow_up_at?: string | null) {
        return axios.post<{ application: MediaApplication }>(`media-applications/${id}/accept`, {
            tier,
            license_product,
            acceptance_note,
            next_follow_up_at,
        });
    },
    approveContent(applicationId: number, updateId: number) {
        return axios.post<{ application: MediaApplication }>(`media-applications/${applicationId}/updates/${updateId}/approve`);
    },
    decline(id: number, reason: string) {
        return axios.post<{ application: MediaApplication }>(`media-applications/${id}/decline`, { reason });
    },
    close(id: number, reason: string) {
        return axios.post<{ application: MediaApplication }>(`media-applications/${id}/close`, { reason });
    },
    updateProgram(
        id: number,
        program_status: MediaProgramStatus,
        next_follow_up_at?: string | null,
        note?: string,
    ) {
        return axios.put<{ application: MediaApplication }>(`media-applications/${id}/program`, {
            program_status,
            next_follow_up_at,
            note,
        });
    },
};
